import { defaultTheme as theme } from '@automata/mui-theme';
import { ParameterDefinition } from '@automata/wfb-api/src/apis/wfbApiSchemas';
import {
  InsertDriveFileOutlined as InsertDriveFileOutlinedIcon,
  NumbersOutlined as NumbersOutlinedIcon,
  TimerOutlined as TimerOutlinedIcon,
  Title as TitleIcon,
  ToggleOffOutlined as ToggleOffOutlinedIcon,
} from '@mui/icons-material';
import { ReactElement } from 'react';
import { SafeParseReturnType } from 'zod';

import { Errors, ParameterType } from './types';
import uuid from '../../../uuid';
import {
  booleanParameterSchema,
  durationParameterSchema,
  fileParameterSchema,
  numberParameterSchema,
  stringParameterSchema,
} from '../../../zodSchemas/parameterReference';

export const getParameterType = (
  parameter: ParameterDefinition | { id: string; type: string }
): ParameterType => {
  if (parameter.type === 'integer' && parameter.id.endsWith('_duration')) {
    return ParameterType.DURATION;
  }

  switch (parameter.type) {
    case 'boolean':
      return ParameterType.BOOLEAN;
    case 'file_ref':
      return ParameterType.FILE;
    case 'float':
      return ParameterType.FLOAT;
    case 'integer':
      return ParameterType.INTEGER;
    default:
      return ParameterType.STRING;
  }
};

export const getParameterIcon = (
  parameterType: ParameterType,
  color: string = theme.palette.grey[700]
): ReactElement => {
  switch (parameterType) {
    case ParameterType.INTEGER:
    case ParameterType.FLOAT:
      return <NumbersOutlinedIcon sx={{ color }} />;
    case ParameterType.BOOLEAN:
      return <ToggleOffOutlinedIcon sx={{ color }} />;
    case ParameterType.DURATION:
      return <TimerOutlinedIcon sx={{ color }} />;
    case ParameterType.FILE:
      return <InsertDriveFileOutlinedIcon sx={{ color }} />;
    default:
      return <TitleIcon sx={{ color }} />;
  }
};

export const getParameterValidationSchema = (parameterType: ParameterType) => {
  switch (parameterType) {
    case ParameterType.INTEGER:
    case ParameterType.FLOAT:
      return numberParameterSchema;
    case ParameterType.BOOLEAN:
      return booleanParameterSchema;
    case ParameterType.DURATION:
      return durationParameterSchema;
    case ParameterType.FILE:
      return fileParameterSchema;
    default:
      return stringParameterSchema;
  }
};

export const getParameterValidationErrors = (
  data: SafeParseReturnType<any, any>
) => {
  if (!data.success) {
    return data.error.errors.reduce((acc, error) => {
      const errorsWithPath = error.path.reduce(
        (acc, field) => (field ? { ...acc, [field]: error } : acc),
        {} as Errors
      );

      return { ...acc, ...errorsWithPath };
    }, {} as Errors);
  } else {
    return {};
  }
};

export const createParameter = (type: ParameterType): ParameterDefinition => {
  let parameter: ParameterDefinition;
  const sharedParams: Omit<ParameterDefinition, 'type' | 'default'> = {
    id: uuid(),
    name: '',
    always_required: false,
  };

  switch (type) {
    case ParameterType.BOOLEAN:
      parameter = {
        ...sharedParams,
        type: 'boolean',
        default: false,
      };
      break;
    case ParameterType.INTEGER:
      parameter = {
        ...sharedParams,
        type: 'integer',
        default: null,
        maximum: null,
        minimum: null,
        multiple_of: null,
      };
      break;
    case ParameterType.FLOAT:
      parameter = {
        ...sharedParams,
        type: 'float',
        default: null,
        maximum: null,
        minimum: null,
      };
      break;
    case ParameterType.DURATION:
      parameter = {
        ...sharedParams,
        id: uuid() + '_duration',
        type: 'integer',
        default: null,
        maximum: null,
        minimum: null,
        multiple_of: null,
      };
      break;
    case ParameterType.FILE:
      parameter = {
        ...sharedParams,
        type: 'file_ref',
        default: '',
      };
      break;
    default:
      parameter = {
        ...sharedParams,
        type: 'string',
        default: '',
        is_directory_path: false,
        is_file_path: false,
      };
  }

  return parameter;
};

export const allParameterTypes: ParameterType[] = [
  ParameterType.INTEGER,
  ParameterType.FLOAT,
  ParameterType.STRING,
  ParameterType.BOOLEAN,
  ParameterType.DURATION,
  ParameterType.FILE,
];
